import logger from '$lib/logger';
import type { Nullable, StarredRoast, TimeTempPair } from '$lib/types/RoastLog';
import * as d3 from 'd3';
import { getXAxisBoundsAndTicks } from './chartUtils';

type SVGChart = d3.Selection<SVGGElement, unknown, HTMLElement, any>;

let chart: Nullable<BurnerChart> = null;
let realtimeLine: any;
let xAxis: d3.Axis<d3.AxisDomain>;
let xScale: any;
let line: (t: TimeTempPair[]) => any;

const timeFormatter = d3.timeFormat('%M:%S');

const colors = d3.schemeCategory10;

const margin = {
  top: 10,
  right: 20,
  bottom: 20,
  left: 30
};

export const DrawEmptyBurnerProfile = (selector: string = 'burner-chart') => {
  chart = new BurnerChart(selector);
  chart.drawEmpty();
  return chart;
};

export const PlotBurnerProfile = (selector: string = 'burner-chart', roast: StarredRoast) => {
  chart = new BurnerChart(selector);
  chart.draw(roast);
  return chart;
};

export class BurnerChart {
  selector: string;
  viewboxWidth!: number;
  viewboxHeight!: number;
  width!: number;
  height!: number;
  chartId: string;
  xMax: number;

  realtimeProfiles!: TimeTempPair[];

  svg!: SVGChart;
  xAxisElement!: SVGChart;
  backgrounLines!: d3.Selection<SVGGElement, TimeTempPair[], SVGGElement, unknown>;
  yAxis!: d3.Axis<d3.NumberValue>;
  backgroundProfiles!: TimeTempPair[];

  private isDarkMode: boolean | undefined;

  constructor(selector: string) {
    this.selector = selector;
    this.chartId = `#${selector}`;
    this.xMax = 0;

    this.realtimeProfiles = [];
    this.isDarkMode = document.querySelector('html')!.classList.contains('dark');
  }

  private setChartDimensions(chartId: string): boolean {
    let chartContainer = document.getElementById(this.selector);

    if (!chartContainer || !chartContainer.parentElement) {
      // First make sure the chart div is there and that we can append to it.
      const mainChartElement = document.getElementById('chart');
      if (!mainChartElement) {
        logger.warn(`Main container not found for burner chart`);
        return false;
      }

      chartContainer = document.createElement('div');
      chartContainer.id = this.selector;
      chartContainer.className = 'mt-[5px] h-full max-h-40';
      mainChartElement.after(chartContainer);
      logger.debug('Created and appended new chart container for burner');
    } else {
      logger.debug('Found existing burner chart element');
    }

    this.viewboxWidth = chartContainer.offsetWidth;
    this.viewboxHeight = chartContainer.offsetHeight;
    this.width = this.viewboxWidth - margin.left - margin.right;
    this.height = this.viewboxHeight - margin.top - margin.bottom;

    return true;
  }

  draw(roast: StarredRoast) {
    const chartReady = this.setChartDimensions(this.selector);
    if (chartReady === false) {
      return false;
    }

    const svg = this.initChart();

    this.setXAxisBoundsAndTicks(roast.profile.plots[0]);
    this.drawAxes(svg);

    this.backgroundProfiles = roast.profile.burner;
    this.drawBackgroundLines(svg);
    this.initRealtimeProfiles(svg);

    this.svg = svg;
  }

  drawEmpty() {
    const chartReady = this.setChartDimensions(this.selector);
    if (chartReady === false) {
      return false;
    }
    this.backgroundProfiles = [
      [0, 10],
      [15, 35],
      [30, 75],
      [120, 35],
      [300, 25],
      [360, 100],
      [420, 50],
      [600, 50]
    ];

    const svg = this.initChart();

    this.setXAxisBoundsAndTicks(this.backgroundProfiles);
    this.drawAxes(svg);
    this.drawBackgroundLines(svg);
    this.initRealtimeProfiles(svg);

    this.svg = svg;
  }

  initChart() {
    d3.select(`${this.chartId} svg`).remove();

    const svg = d3
      .select(this.chartId)
      .append('svg')
      .attr('viewBox', `0 0 ${this.viewboxWidth} ${this.viewboxHeight}`)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // scales
    xScale = d3.scaleTime().domain([0, 60]).range([0, this.width]);
    const yScale = d3.scaleLinear().domain([0, 100]).range([this.height, 0]);

    // axes
    xAxis = d3.axisBottom(xScale).tickFormat((t: any) => timeFormatter(new Date(t * 1000)));
    xAxis.ticks(d3.timeMillisecond.every(60));

    this.yAxis = d3.axisLeft(yScale);

    // x-axis
    line = d3
      .line()
      .x((d: TimeTempPair) => xScale(d[0]))
      .y((d: TimeTempPair) => yScale(d[1]))
      .curve(d3.curveStepBefore);

    return svg;
  }

  update(data: TimeTempPair[]) {
    this.realtimeProfiles = data;

    const redrawAxes = this.setXAxisBoundsAndTicks(this.realtimeProfiles);

    if (redrawAxes) {
      this.xAxisElement.transition().duration(1000).call(xAxis);
      // this.backgrounLines
      //   .select('path.line')
      //   .transition()
      //   .duration(1000)
      //   .attr('d', line(this.realtimeProfiles));
    }

    realtimeLine.attr('d', line(this.realtimeProfiles));
  }

  clearChart() {
    this.svg.selectAll('path.line').remove();
    this.svg.selectAll('.profile').remove();
  }

  private drawAxes(svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>) {
    this.xAxisElement = svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${this.height})`)
      .call(xAxis);

    // y-axis
    svg.append('g').attr('class', 'y-axis').call(this.yAxis);
  }

  private drawBackgroundLines(svg: SVGChart) {
    this.backgrounLines = svg
      .selectAll('.bgprofile')
      .data([this.backgroundProfiles])
      .enter()
      .append('g')
      .attr('class', 'profile burner-bgprofile');

    this.backgrounLines
      .append('path')
      .attr('class', 'line')
      .attr('d', line(this.backgroundProfiles))
      .style('stroke', (d, i) => colors[i])
      .style('opacity', this.isDarkMode ? 0.3 : 0.2)
      .style('stroke-width', 2);
  }

  private initRealtimeProfiles(svg: SVGChart) {
    if (this.realtimeProfiles.length < 1) {
      this.realtimeProfiles = [[0, 0]];
    }
    realtimeLine = svg
      .selectAll('.realtime-profile')
      .data(this.realtimeProfiles)
      .enter()
      .append('g')
      .attr('class', 'profile power-realtime')
      .append('path')
      .attr('class', 'line')
      .style('stroke', (d, i) => colors[i])
      .style('stroke-width', 2);
  }

  private setXAxisBoundsAndTicks(data: TimeTempPair[]): boolean {
    const { redrawChart, ticks, xUpperBound } = getXAxisBoundsAndTicks(data, this.xMax);

    if (xUpperBound > this.xMax) {
      this.xMax = xUpperBound;
    }

    if (redrawChart) {
      xScale.domain([0, xUpperBound]);
      xAxis.tickValues(ticks!);
    }

    return redrawChart;
  }
}
